import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import EmojiPickerReact, { EmojiClickData, Theme } from "emoji-picker-react";

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
  placeholder?: string;
}

export function EmojiPicker({ value, onChange, placeholder }: EmojiPickerProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('emojiPicker.chooseEmoji');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Schließen bei Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Escape-Taste zum Schließen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onChange(emojiData.emoji);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between min-h-[52px] hover:bg-gray-50 transition-colors"
      >
        {value ? (
          <span className="text-3xl">{value}</span>
        ) : (
          <span className="text-gray-400">{resolvedPlaceholder}</span>
        )}
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Emoji Picker Dropdown */}
      {isOpen && (
        <div className="absolute z-[100] mt-1 left-0">
          <EmojiPickerReact
            onEmojiClick={handleEmojiClick}
            theme={Theme.LIGHT}
            searchPlaceHolder={t('emojiPicker.searchEmoji')}
            width={350}
            height={400}
            previewConfig={{
              showPreview: false,
            }}
            skinTonesDisabled
            lazyLoadEmojis
          />
        </div>
      )}
    </div>
  );
}
